import { Component, ElementRef, viewChildren, inject, signal, computed, effect, DestroyRef, input } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { debounceTime } from 'rxjs';
import { PATH_RELOAD_PAGES } from 'src/app/global';
import { ContainerId, allDrawerIds } from '../../../models/drawer';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MedicationService } from 'src/app/calvin-emr-shared/services/medication.service';
import { Medication } from 'src/app/calvin-emr-shared/models/medication.model';
import { ContainersService } from 'src/app/knightmeds/services/containers.service';
import { PageComponent } from 'src/app/calvin-emr-shared/page/page.component';
import { ProgressBar } from 'primeng/progressbar';
import { NgClass } from '@angular/common';
import { InputNumber } from 'primeng/inputnumber';
import { KnightmedsDiagramComponent } from 'src/app/knightmeds/knightmeds-diagram/knightmeds-diagram.component';
import { ButtonModule } from 'primeng/button';

/** A page used to specify what was reloaded into a container */
@Component({
  selector: 'app-reload-page',
  templateUrl: './reload-page.component.html',
  styleUrls: ['./reload-page.component.scss'],
  standalone: true,
  imports: [
    ProgressBar,
    NgClass,
    ReactiveFormsModule,
    InputNumber,
    KnightmedsDiagramComponent,
    ButtonModule,
  ],
})
export class ReloadPageComponent extends PageComponent {
  /** The id of the container being opened */
  containerId = signal<ContainerId | null>(null);

  /** Whether the container has been processed (opened and pre-filled) */
  processedContainerId = signal(false);

  /** The currently highlighted container id in the diagram */
  highlightedContainerId = signal<ContainerId | undefined>(undefined);

  /** Holds the value in the medication id input */
  medicationId = new FormControl<string>("");

  /** The medication corresponding to the current medication id */
  medication = signal<Medication | undefined>(undefined);

  /** Holds the value in the quantity input */
  quantity = new FormControl<number | undefined>(undefined);

  /** Whether any containers are open */
  anyContainersOpen = false;

  /** A reference of the medication id input used to auto select its contents */
  medicationIdInput = viewChildren<ElementRef>('medicationIdInput');

  readonly containerIdParam = input<string>(undefined, { alias: 'container-id' });

  private router = inject(Router);
  private medicationService = inject(MedicationService);
  private containersService = inject(ContainersService);
  private destroyRef = inject(DestroyRef);

  // medicationId.value is a plain FormControl property, not a signal --
  // computed()/effect() only react to signal reads, so anything derived
  // straight from .value would compute once (at construction, likely
  // before the field even has a value) and then never update again as the
  // user types. Bridge valueChanges into an actual signal so downstream
  // computed()s stay live. Debounced so the medication lookup (and
  // everything derived from it) doesn't fire a Firestore read on every
  // keystroke -- only once typing pauses.
  private medicationIdValue = toSignal(
    this.medicationId.valueChanges.pipe(debounceTime(300)),
    { initialValue: this.medicationId.value }
  );

  /** Cleaned medication ID from the form control */
  private cleanedMedicationId = computed(() => {
    const val = this.medicationIdValue();
    return val ? val.toUpperCase().trim() : '';
  });

  /** Whether the medication ID is valid (has a matching medication or is empty) */
  isValidMedicationId = computed(() => {
    const med = this.medication();
    const cleaned = this.cleanedMedicationId();
    return med != undefined || !cleaned || cleaned === '';
  });

  /** Description of the current medication state */
  medicationString = computed(() => {
    const med = this.medication();
    const cleaned = this.cleanedMedicationId();
    if (med) {
      return med.name;
    }
    if (!cleaned || cleaned === '') {
      return 'None';
    }
    return 'No medication has this ID';
  });

  /**
   * Whether the container's data has finished loading. Deliberately does
   * NOT factor in isValidMedicationId() -- that reflects the currently
   * typed id and legitimately flips true/false live as the user types
   * (already surfaced separately via the red-highlight styling and the
   * disabled Finished button), whereas "loaded" should be a one-way
   * transition that never flips back to false once the container's data
   * has arrived. Tying the two together made the whole page flash back to
   * the progress bar mid-edit, right as a newly-typed id passed cleaning
   * but before its Firestore lookup had resolved.
   */
  loaded = computed(() => {
    return this.containerId() !== null && this.processedContainerId();
  });

  constructor() {
    super();

    // Auto-select the medication ID input once it appears in the DOM
    effect(() => {
      const inputs = this.medicationIdInput();
      if (inputs.length > 0) {
        inputs[0].nativeElement.select();
      }
    });

    // When medication ID changes, look up the medication
    effect(() => {
      const cleaned = this.cleanedMedicationId();
      if (!cleaned || cleaned === '') {
        this.medication.set(undefined);
        return;
      }

      this.medicationService.getMedication(cleaned)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(snapshot => {
          // snapshot.payload.exists is a method on the modular Firestore
          // DocumentSnapshot, not a boolean property -- accessing it
          // uncalled always returns a (truthy) function reference, so this
          // must be called to actually detect a missing document.
          this.medication.set(
            snapshot.payload.exists() ? MedicationService.getMedicationFromSnapshot(snapshot) : undefined
          );
        });
    });
  }

  override ngOnInit(): void {
    super.ngOnInit();

    const containerIdString = this.containerIdParam();

    if (containerIdString == null) {
      this.router.navigate(['/', PATH_RELOAD_PAGES]);
      return;
    }

    const containerId = parseInt(containerIdString) as ContainerId;

    if (isNaN(containerId) || !allDrawerIds.includes(containerId)) {
      this.router.navigate(['/', PATH_RELOAD_PAGES]);
      return;
    }

    this.containerId.set(containerId);
    this.openAndPrefillContainer(containerId);

    // Listen for container open/close events
    this.containersService.addAnyContainersOpenListener((anyContainersOpen) => {
      this.anyContainersOpen = anyContainersOpen;
    });
  }

  /**
   * Opens the given container's relay and pre-fills the form with its
   * current medication id/quantity. Plain imperative code rather than an
   * effect() -- containerId is only ever set once (right above, in
   * ngOnInit), so there's no ongoing reactivity to wire up here.
   */
  private openAndPrefillContainer(containerId: ContainerId): void {
    this.containersService.openContainer(containerId);
    this.highlightedContainerId.set(containerId);

    this.containersService.getContainerById(containerId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(container => {
        this.medicationId.setValue(container.medicationId ?? '');
        this.quantity.setValue(container.quantity);
        this.processedContainerId.set(true);
      });
  }

  closeContainer(): void {
    const id = this.containerId();
    if (id !== null) {
      this.highlightedContainerId.set(undefined);
      this.containersService.closeContainer(id);
    }
  }

  /** Record the medication id and quantity entered then navigate back to the containers page */
  onFinished(containerId: ContainerId) {
    this.containersService.setMedicationId(
      containerId,
      this.medicationId.value ? this.medicationId.value.toUpperCase().trim() : undefined
    );
    this.containersService.setQuantity(
      containerId,
      this.medicationId.value ? (this.quantity.value ?? undefined) : undefined
    );

    this.router.navigate(['/', PATH_RELOAD_PAGES]);
  }
}
